import { t } from '../../i18n';
// admin-client/src/components/lounge/LoungeItemModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DEFAULT_CATEGORIES, normalizeLoungeCategoryOptions, normalizeLoungeCategoryValue, uploadLoungeImage, } from '../../api/loungeApi';
import LoungeWidgetManager from './LoungeWidgetManager';
import LoungeRichEditor, { createLoungeTextBlock, normalizeLoungeEditorBlocks } from './LoungeRichEditor';
function uid() {
    return Math.random().toString(36).slice(2, 10);
}
function emptyTextBlock() {
    return createLoungeTextBlock();
}
function emptyPaymentOption() {
    return {
        localId: uid(),
        optionCode: '',
        label: '',
        unitAmount: '',
        stockQuantity: 0,
        soldQuantity: 0,
        remainingQuantity: 0,
        status: 'active',
    };
}
function normalizeBlocks(value) {
    return normalizeLoungeEditorBlocks(value);
}
function normalizePaymentOptions(value) {
    if (!Array.isArray(value))
        return [];
    return value.map((raw, idx) => {
        const stock = Math.max(0, Number(raw.stockQuantity ?? raw.stock_quantity ?? 0));
        const sold = Math.max(0, Number(raw.soldQuantity ?? raw.sold_quantity ?? 0));
        return {
            localId: raw.localId || raw.optionCode || raw.option_code || uid(),
            optionCode: String(raw.optionCode ?? raw.option_code ?? '').trim(),
            label: String(raw.label ?? '').trim(),
            unitAmount: Number(raw.unitAmount ?? raw.unit_amount ?? 0) || '',
            stockQuantity: stock,
            soldQuantity: sold,
            remainingQuantity: Math.max(0, Number(raw.remainingQuantity ?? raw.remaining_quantity ?? stock - sold)),
            status: String(raw.status || 'active'),
            sortOrder: Number(raw.sortOrder ?? raw.sort_order ?? idx),
        };
    });
}
function normalizeInitial(v = {}) {
    return {
        postCode: v.postCode || '',
        category: normalizeLoungeCategoryValue(v.category),
        title: v.title || '',
        thumbnailUrl: v.thumbnailUrl || '',
        contentJson: normalizeBlocks(v.contentJson),
        status: v.status || 'draft',
        uploadFee: Number(v.uploadFee || 60),
        periodDays: Number(v.periodDays || 30),
        publishEndAt: v.publishEndAt || v.expiredAt || null,
        expiredAt: v.expiredAt || v.publishEndAt || null,
        isPaid: Boolean(v.isPaid),
        paymentOptions: normalizePaymentOptions(v.paymentOptions ?? v.payment_options),
    };
}
function buildPublicLoungeUrl(postCode) {
    const code = String(postCode || '').trim();
    if (!code)
        return '';
    const configuredBase = String(process.env.REACT_APP_USER_APP_URL || '').trim();
    const defaultBase = process.env.NODE_ENV === 'development'
        ? 'http://localhost:3001'
        : 'https://app.nightpokerclub.com';
    const base = (configuredBase || defaultBase).replace(/\/+$/, '');
    return `${base}/lobby/lounge/${encodeURIComponent(code)}`;
}
async function copyTextToClipboard(value) {
    const text = String(value || '');
    if (!text)
        return false;
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    }
    catch (_) { }
    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    }
    catch (_) {
        return false;
    }
}
export default function LoungeItemModal({ open, mode, initialValue, uploadFee = 60, categories = DEFAULT_CATEGORIES, submitting = false, onClose, onSave, onUpload, canPublish = true, }) {
    const isEdit = mode === 'edit';
    const categoryOptions = useMemo(() => normalizeLoungeCategoryOptions(categories), [categories]);
    const thumbnailRef = useRef(null);
    const contentImageRef = useRef(null);
    const contentImageInsertAfterRef = useRef(null);
    const initial = useMemo(() => normalizeInitial(initialValue), [initialValue]);
    const [form, setForm] = useState(initial);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [urlCopyState, setUrlCopyState] = useState('');
    useEffect(() => {
        if (open) {
            setForm(initial);
            setError('');
            setUrlCopyState('');
        }
    }, [open, initial]);
    useEffect(() => {
        if (!open)
            return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting && !uploading)
                onClose?.();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose, submitting, uploading]);
    useEffect(() => {
        if (!open)
            return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);
    if (!open)
        return null;
    const disabled = submitting || uploading;
    const loungeUrl = isEdit ? buildPublicLoungeUrl(form.postCode) : '';
    const copyLoungeUrl = async () => {
        if (!loungeUrl)
            return;
        const copied = await copyTextToClipboard(loungeUrl);
        setUrlCopyState(copied ? 'copied' : 'failed');
        window.setTimeout(() => setUrlCopyState(''), 1600);
    };
    const setPaymentOption = (idx, patch) => {
        setForm((prev) => ({
            ...prev,
            paymentOptions: prev.paymentOptions.map((option, i) => (i === idx ? { ...option, ...patch } : option)),
        }));
    };
    const addPaymentOption = () => {
        setForm((prev) => ({ ...prev, paymentOptions: [...prev.paymentOptions, emptyPaymentOption()] }));
    };
    const removePaymentOption = (idx) => {
        setForm((prev) => {
            const target = prev.paymentOptions[idx];
            if (!target)
                return prev;
            if (target.optionCode) {
                return {
                    ...prev,
                    paymentOptions: prev.paymentOptions.map((option, i) => (i === idx ? { ...option, status: 'deleted' } : option)),
                };
            }
            return { ...prev, paymentOptions: prev.paymentOptions.filter((_, i) => i !== idx) };
        });
    };
    const uploadFile = async (file, type) => {
        if (!file)
            return null;
        setError('');
        setUploading(true);
        try {
            const item = await uploadLoungeImage(file, type);
            if (!item?.url)
                throw new Error(t("\uC5C5\uB85C\uB4DC URL\uC744 \uBC1B\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4."));
            return item.url;
        }
        catch (err) {
            setError(err?.message || t("\uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC\uC5D0 \uC2E4\uD328\uD588\uC2B5\uB2C8\uB2E4."));
            return null;
        }
        finally {
            setUploading(false);
        }
    };
    const onThumbnailChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        const url = await uploadFile(file, 'thumbnail');
        if (!url)
            return;
        setForm((prev) => ({ ...prev, thumbnailUrl: url }));
    };
    const requestContentImageUpload = (insertAfterIndex = null) => {
        if (disabled)
            return;
        contentImageInsertAfterRef.current = Number.isInteger(insertAfterIndex) ? insertAfterIndex : null;
        contentImageRef.current?.click();
    };
    const onContentImageChange = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        const insertAfterIndex = contentImageInsertAfterRef.current;
        contentImageInsertAfterRef.current = null;
        const url = await uploadFile(file, 'content');
        if (!url)
            return;
        setForm((prev) => {
            const current = normalizeLoungeEditorBlocks(prev.contentJson);
            const imageBlock = { id: uid(), type: 'image', url, alt: '', display: { widthPercent: 100, heightPx: 280, objectFit: 'clip' } };
            const next = [...current];
            const targetIndex = Number.isInteger(insertAfterIndex)
                ? Math.max(0, Math.min(insertAfterIndex + 1, next.length))
                : next.length;
            next.splice(targetIndex, 0, imageBlock);
            if (!next[targetIndex + 1] || next[targetIndex + 1].type !== 'text') {
                next.splice(targetIndex + 1, 0, emptyTextBlock());
            }
            return { ...prev, contentJson: next };
        });
    };
    const buildPaymentOptionsPayload = () => {
        return form.paymentOptions.map((option, idx) => {
            const status = String(option.status || 'active');
            const optionCode = String(option.optionCode || '').trim();
            if (status === 'deleted') {
                return { optionCode, status: 'deleted', sortOrder: idx };
            }
            const label = String(option.label || '').trim();
            const unitAmountText = String(option.unitAmount ?? '').trim();
            const unitAmount = Number(unitAmountText);
            const stockQuantity = Number(option.stockQuantity);
            const soldQuantity = Number(option.soldQuantity || 0);
            if (!label)
                throw new Error(t("\uACB0\uC81C \uD56D\uBAA9\uBA85\uC744 \uC785\uB825\uD558\uC138\uC694."));
            if (!/^\d+(?:\.\d{1,2})?$/.test(unitAmountText) || !Number.isFinite(unitAmount) || unitAmount <= 0)
                throw new Error(t("\uACB0\uC81C \uD56D\uBAA9 \uAE08\uC561\uC740 0\uBCF4\uB2E4 \uD06C\uACE0 \uC18C\uC218 \uB458\uC9F8 \uC790\uB9AC\uAE4C\uC9C0\uB9CC \uC785\uB825\uD560 \uC218 \uC788\uC2B5\uB2C8\uB2E4."));
            if (!Number.isInteger(stockQuantity) || stockQuantity < 0)
                throw new Error(t("\uACB0\uC81C \uD56D\uBAA9 \uC218\uB7C9\uC740 0 \uC774\uC0C1 \uC815\uC218\uC5EC\uC57C \uD569\uB2C8\uB2E4."));
            if (optionCode && stockQuantity < soldQuantity)
                throw new Error(t("\uACB0\uC81C \uD56D\uBAA9 \uC218\uB7C9\uC740 \uC774\uBBF8 \uD310\uB9E4\uB41C \uC218\uB7C9\uBCF4\uB2E4 \uC791\uAC8C \uC218\uC815\uD560 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4."));
            return {
                optionCode,
                label,
                unitAmount,
                stockQuantity,
                status: 'active',
                sortOrder: idx,
            };
        });
    };
    const normalizeWidgetAssets = blocks => blocks.map(b => b.type !== 'widget' ? b : ({
      ...b,
      offers: (b.widgetType === 'shop' ? b.offers || [] : []).map(o => ({...o, imageUrl: (() => {
        if (!o.imageUrl) return '';
        try {return new URL(o.imageUrl,window.location.origin).pathname;} catch(_) {return o.imageUrl;}
      })()})),
    }));
    const payload = (status) => ({
        category: form.category,
        title: form.title.trim(),
        thumbnail_url: form.thumbnailUrl,
        content_json: normalizeWidgetAssets(normalizeLoungeEditorBlocks(form.contentJson)),
        payment_options: buildPaymentOptionsPayload(),
        status,
    });
    const getSafeSaveStatus = () => {
        if (!isEdit)
            return 'draft';
        const currentStatus = String(form.status || initialValue?.status || 'draft');
        if (currentStatus === 'published') {
            const endAt = form.publishEndAt || form.expiredAt || initialValue?.publishEndAt || initialValue?.expiredAt || null;
            const hasActivePeriod = !!endAt && new Date(endAt).getTime() > Date.now();
            return hasActivePeriod ? 'published' : 'paused';
        }
        if (currentStatus === 'paused')
            return 'paused';
        return 'draft';
    };
    const submitDraft = () => {
        if (disabled)
            return;
        try {
            setError('');
            onSave?.(payload(getSafeSaveStatus()));
        }
        catch (err) {
            setError(err.message || t("\uC785\uB825\uAC12\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."));
        }
    };
    const submitPublish = () => {
        if (disabled)
            return;
        try {
            setError('');
            const fee = Number(form.uploadFee || uploadFee || 60);
            const days = Number(form.periodDays || initialValue?.periodDays || 30);
            const endAt = form.publishEndAt || form.expiredAt || initialValue?.publishEndAt || initialValue?.expiredAt || null;
            const hasActivePeriod = !!endAt && new Date(endAt).getTime() > Date.now();
            const needPayment = !hasActivePeriod;
            if (needPayment) {
                const ok = window.confirm(t('업로드 비용 {fee} USDT가 UserDB.asset에서 차감되고, 결제일 기준 {days}일 동안 게시됩니다. 게시할까요?', { fee: fee.toLocaleString(), days }));
                if (!ok)
                    return;
            }
            onUpload?.(payload('published'));
        }
        catch (err) {
            setError(err.message || t("\uC785\uB825\uAC12\uC744 \uD655\uC778\uD574 \uC8FC\uC138\uC694."));
        }
    };
    const visiblePaymentOptions = form.paymentOptions
        .map((option, idx) => ({ option, idx }))
        .filter(({ option }) => option.status !== 'deleted');
    return createPortal((<div className="lounge-modal-root fixed inset-0 overflow-y-auto" style={{ zIndex: 10000 }} role="dialog" aria-modal="true" aria-labelledby="lounge-modal-title">
      <div className="lounge-modal-backdrop fixed inset-0 bg-black/40" onClick={() => !disabled && onClose?.()}/>

      <div className="lounge-modal-frame relative min-h-full flex items-start justify-center px-3 py-4 sm:px-4 sm:py-8">
        <div className="lounge-modal-panel w-full max-w-5xl bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden mb-10">
          <div className="lounge-modal-header flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div>
              <div id="lounge-modal-title" className="text-sm font-bold text-gray-900">{t("\uB77C\uC6B4\uC9C0 \uAE00")}{isEdit ? t("\uC218\uC815") : t("\uC791\uC131")}</div>
              <div className="text-xs text-gray-500 mt-1">{t("\uC800\uC7A5\uC740 \uBB34\uB8CC\uC785\uB2C8\uB2E4. \uCD5C\uCD08 \uC5C5\uB85C\uB4DC/\uAC8C\uC2DC \uC2DC\uC5D0\uB9CC \uC5D0\uC774\uC804\uD2B8 \uC790\uC0B0\uC5D0\uC11C \uBE44\uC6A9\uC774 \uCC28\uAC10\uB429\uB2C8\uB2E4.")}</div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={submitDraft} disabled={disabled} className="px-4 py-2 rounded-md bg-gray-500 hover:bg-gray-600 text-white text-sm font-semibold disabled:opacity-50">{t("\uC800\uC7A5")}</button>
              {canPublish && <button type="button" onClick={submitPublish} disabled={disabled} className="px-4 py-2 rounded-md bg-yellow-400 hover:bg-yellow-500 text-black text-sm font-semibold disabled:opacity-50">{t("\uC5C5\uB85C\uB4DC/\uAC8C\uC2DC")}</button>}
              <button type="button" onClick={onClose} disabled={disabled} className="ml-1 w-9 h-9 rounded-md hover:bg-gray-100 text-gray-600 disabled:opacity-50" aria-label="close" title={t("\uB2EB\uAE30")}>
                ✕
              </button>
            </div>
          </div>

          <div className="p-4 sm:p-6 space-y-6">
            {error ? (<div className="rounded-md bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
                {error}
              </div>) : null}

            {uploading ? (<div className="rounded-md bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 text-sm">{t("\uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC \uC911...")}</div>) : null}

            <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-5">
              <div>
                <div className="text-xs font-semibold text-gray-700 mb-2">{t("\uC378\uB124\uC77C \uC774\uBBF8\uC9C0")}</div>
                <button type="button" onClick={() => thumbnailRef.current?.click()} disabled={disabled} className="w-full h-36 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center disabled:opacity-50" title={t("\uD074\uB9AD\uD558\uC5EC \uC774\uBBF8\uC9C0 \uC5C5\uB85C\uB4DC")}>
                  {form.thumbnailUrl ? (<img src={form.thumbnailUrl} alt="thumbnail" className="w-full h-full object-cover"/>) : (<div className="text-gray-400 text-2xl">🖼️</div>)}
                </button>
                <div className="flex items-center gap-2 mt-3">
                  <button type="button" onClick={() => thumbnailRef.current?.click()} disabled={disabled} className="px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm disabled:opacity-50">{t("\uC5C5\uB85C\uB4DC")}</button>
                  <button type="button" onClick={() => setForm((p) => ({ ...p, thumbnailUrl: '' }))} disabled={disabled} className="px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm disabled:opacity-50">{t("\uC81C\uAC70")}</button>
                  <input ref={thumbnailRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onThumbnailChange}/>
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr] sm:items-center gap-1 sm:gap-2">
                  <div className="text-xs font-semibold text-gray-700">{t("\uCE74\uD14C\uACE0\uB9AC")}</div>
                  <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-gray-50" disabled={disabled}>
                    {categoryOptions.map((c) => (<option key={c.value} value={c.value}>{c.label}</option>))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr] sm:items-center gap-1 sm:gap-2">
                  <div className="text-xs font-semibold text-gray-700">{t("\uC81C\uBAA9")}</div>
                  <input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} placeholder={t("\uC81C\uBAA9\uC744 \uC785\uB825\uD558\uC138\uC694")} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" disabled={disabled}/>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr] sm:items-center gap-1 sm:gap-2">
                  <div className="text-xs font-semibold text-gray-700">{t("\uC5C5\uB85C\uB4DC \uBE44\uC6A9")}</div>
                  <div className="rounded-md bg-yellow-100 border border-yellow-200 px-3 py-2 text-sm font-semibold text-yellow-900">
                    {Number(form.periodDays || initialValue?.periodDays || 30)}{t("\uC77C /")}{Number(form.uploadFee || uploadFee || 60).toLocaleString()} USDT
                    {form.isPaid ? <span className="ml-2 text-emerald-700">{t("\uACB0\uC81C \uC644\uB8CC")}</span> : <span className="ml-2 text-gray-500">{t("\uBBF8\uACB0\uC81C")}</span>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-[90px_1fr] sm:items-center gap-1 sm:gap-2">
                  <div className="text-xs font-semibold text-gray-700">{t("\uC0C1\uD0DC")}</div>
                  <div className="text-sm text-gray-600">{form.status}</div>
                </div>

                {loungeUrl ? (<div className="grid grid-cols-1 sm:grid-cols-[76px_1fr] sm:items-center gap-1">
                  <div className="text-[11px] font-semibold text-gray-700">{t("\uAC8C\uC2DC\uAE00 URL")}</div>
                  <div className="flex min-w-0 max-w-xl items-center gap-1.5">
                    <input value={loungeUrl} readOnly onFocus={(e) => e.currentTarget.select()} className="min-w-0 flex-1 border border-gray-200 rounded bg-gray-50 px-2 py-1 text-[11px] leading-4 text-gray-700" aria-label={t("\uB77C\uC6B4\uC9C0 \uAC8C\uC2DC\uAE00 URL")}/>
                    <button type="button" onClick={copyLoungeUrl} className="shrink-0 px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-[11px] leading-4 font-semibold">
                      {urlCopyState === 'copied' ? t("\uBCF5\uC0AC\uB428") : urlCopyState === 'failed' ? t("\uBCF5\uC0AC \uC2E4\uD328") : t("\uBCF5\uC0AC")}
                    </button>
                  </div>
                </div>) : null}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm font-bold text-gray-900">{t("\uACB0\uC81C \uC635\uC158")}</div>
                  <div className="text-xs text-gray-500 mt-1">{t("\uB77C\uC6B4\uC9C0 \uC0C1\uC138\uC5D0\uC11C \uD56D\uBAA9\uBCC4 \uC218\uB7C9 \uC785\uB825 + \uACB0\uC81C \uBC84\uD2BC\uC73C\uB85C \uD45C\uC2DC\uB429\uB2C8\uB2E4.")}</div>
                </div>
                <button type="button" disabled={disabled} onClick={addPaymentOption} className="w-full sm:w-auto px-3 py-2 rounded-md border border-gray-200 hover:bg-gray-50 text-sm disabled:opacity-50">{t("\uACB0\uC81C \uC635\uC158 \uCD94\uAC00")}</button>
              </div>

              <div className="space-y-3">
                {visiblePaymentOptions.length ? visiblePaymentOptions.map(({ option, idx }) => (<div key={option.localId || option.optionCode || idx} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_120px_120px_90px] gap-2 items-center rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <input value={option.label} onChange={(e) => setPaymentOption(idx, { label: e.target.value })} placeholder={t("\uACB0\uC81C \uD56D\uBAA9")} disabled={disabled} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"/>
                    <input type="number" inputMode="decimal" min="0.01" step="0.01" value={option.unitAmount} onChange={(e) => setPaymentOption(idx, { unitAmount: e.target.value })} placeholder={t("\uAE08\uC561")} disabled={disabled} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"/>
                    <input type="number" min={Math.max(0, Number(option.soldQuantity || 0))} step="1" value={option.stockQuantity} onChange={(e) => setPaymentOption(idx, { stockQuantity: e.target.value === '' ? '' : Number(e.target.value) })} placeholder={t("\uC218\uB7C9")} disabled={disabled} className="border border-gray-200 rounded-md px-3 py-2 text-sm bg-white"/>
                    <button type="button" disabled={disabled} onClick={() => removePaymentOption(idx)} className="px-3 py-2 rounded-md border border-gray-200 bg-white hover:bg-red-50 text-sm text-red-600 disabled:opacity-50">{t("\uC0AD\uC81C")}</button>
                    {option.optionCode ? (<div className="sm:col-span-2 lg:col-span-4 text-[11px] text-gray-500">{t("\uD310\uB9E4")}{Number(option.soldQuantity || 0).toLocaleString()}{t("\uAC1C \u00B7 \uB0A8\uC740 \uC218\uB7C9")}{Math.max(0, Number(option.stockQuantity || 0) - Number(option.soldQuantity || 0)).toLocaleString()}{t("\uAC1C")}</div>) : null}
                  </div>)) : (<div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-500">{t("\uB4F1\uB85D\uB41C \uACB0\uC81C \uC635\uC158\uC774 \uC5C6\uC2B5\uB2C8\uB2E4.")}</div>)}
              </div>
            </div>

            <div className="border-t border-gray-100 pt-5">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
                <div>
                  <div className="text-sm font-bold text-gray-900">{t("본문 에디터")}</div>
                  <div className="text-xs text-gray-500 mt-1">{t("일반 블로그처럼 큰 편집 영역에서 글을 작성하고, 상단 도구로 텍스트 스타일·링크·이미지를 넣을 수 있습니다.")}</div>
                </div>
                <div className="text-[11px] text-gray-400">{t("기존 텍스트/이미지 블록 형식으로 자동 저장됩니다.")}</div>
              </div>

              <LoungeWidgetManager
                blocks={form.contentJson}
                onChange={(contentJson)=>setForm(p=>({...p,contentJson}))}
                disabled={disabled}
                onUploadImage={async file=>{
                  const url=await uploadFile(file,'content');
                  if(!url) return null;
                  try {return new URL(url,window.location.origin).pathname;} catch(_) {return url;}
                }}
              />
              <LoungeRichEditor
                value={form.contentJson}
                onChange={(contentJson) => setForm((prev) => ({ ...prev, contentJson }))}
                disabled={disabled}
                uploading={uploading}
                onRequestImageUpload={requestContentImageUpload}
              />
              <input ref={contentImageRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onContentImageChange}/>
            </div>

            <div className="text-xs text-gray-400">
              {isEdit ? t("\uC218\uC815 \uBAA8\uB4DC") : t("\uC2E0\uADDC \uC791\uC131")}{t("\u00B7 \uC11C\uBC84\uC5D0\uC11C \uCE74\uD14C\uACE0\uB9AC/\uC0C1\uD0DC/\uBE44\uC6A9/\uAD8C\uD55C\uC744 \uB2E4\uC2DC \uAC80\uC99D\uD569\uB2C8\uB2E4.")}</div>
          </div>
        </div>
      </div>
    </div>), document.body);
}
